/**
 * Marketing copy for the FFS-MNT landing page.
 *
 * Mirrors https://www.nourishedrx.com/dietitian-support so a member who arrives from the marketing
 * site sees the same page. Copy is held here rather than inline so the wording is reviewable in one
 * place — it makes coverage and cost claims, which are not ours to paraphrase casually.
 *
 * THREE items are deliberately ADAPTED from the marketing site, because that page describes the
 * legacy submit-and-follow-up flow and this app does something different. Each is marked ADAPTED
 * with the original text. Everything else is verbatim.
 */

export interface FaqItem {
  readonly question: string;
  readonly answer: string;
  /** Rendered as a bulleted list under {@link answer} when present. */
  readonly bullets?: readonly string[];
}

export const HERO = {
  headingLead: 'Feel better, eat better, and get the support you need – ',
  headingAccent: 'through your benefits',
  leadStrong: 'Meet with a dietitian for as little as $0.',
  lead: 'NourishedRx helps you access nutrition support through insurance – from the comfort of your home. Here is how it works:',
  checklist: [
    'Check your eligibility',
    'Book your first session',
    'Meet with a dietitian and create your plan',
  ],
  cta: 'Check your eligibility here',
  contact:
    'You can text us at 833-349-0399, Monday to Friday, 8 a.m. to 8 p.m. ET, or email us at consults@nourishedrx.com.',
} as const;

export const INSURANCE_BAND = 'Covered by many major insurance plans';

export const SUPPORT_POINTS: readonly { title: string; body: string }[] = [
  {
    title: 'Meet from home, on your schedule.',
    body: 'One-on-one virtual support from a registered dietitian, from the comfort of your home.',
  },
  {
    title: 'Support tailored to your health needs',
    body: 'Personalized guidance to help you build sustainable eating habits, reach your health goals, and feel your best.',
  },
  {
    title: 'Insurance may cover part or all of the cost',
    body: 'Coverage, eligibility, and out-of-pocket costs vary by plan.',
  },
  {
    title: 'Simple sign-up and easy scheduling',
    body: 'You can call, text, or email us to get started.',
  },
];

export const WHY = {
  heading: 'Why work with a dietitian through NourishedRx',
  body: 'Not all nutrition care is created equal. NourishedRx goes beyond basic advice or one-time visits. Our approach is rooted in the idea that food is a key part of health. We combine clinical expertise with personalized, practical support to help you make changes that actually fit your life—and last over time.',
  conditionsHeading: 'We can help if you’re trying to:',
} as const;

/** `tint` is a CSS custom property holding the card background. */
export const CONDITIONS: readonly {
  label: string;
  icon: string;
  alt: string;
  tint: string;
  /**
   * Rendered width in px, per icon, matching how nourishedrx.com draws each one (measured at a
   * 1440 viewport). These six icons have six different aspect ratios, so a single shared size
   * either distorts them or leaves the tall narrow ones looking undersized next to the squat wide
   * ones. The marketing site tunes each individually; this mirrors that. Height follows from the
   * icon's own aspect ratio — never set it, or they stretch again.
   */
  iconWidth: number;
}[] = [
  {
    label: 'Lower blood sugar or manage diabetes',
    icon: '/images/icon-blood-sugar.webp',
    alt: 'blood sugar icon',
    iconWidth: 76,
    tint: 'var(--tint-mint)',
  },
  {
    label: 'Eat healthier without feeling overwhelmed',
    icon: '/images/icon-eat-healthier.webp',
    alt: 'fork and spoon surrounded by a heart icon',
    iconWidth: 76,
    tint: 'var(--tint-mint)',
  },
  {
    label: 'Maintain muscle while losing weight',
    icon: '/images/icon-maintain-muscle.webp',
    alt: 'person stretching icon',
    iconWidth: 50,
    tint: 'var(--tint-sand)',
  },
  {
    label: 'Improve heart health or blood pressure',
    icon: '/images/icon-blood-pressure.webp',
    alt: 'blood pressure monitor icon',
    iconWidth: 68,
    tint: 'var(--tint-lime)',
  },
  {
    label: 'Manage side effects on GLP-1s',
    icon: '/images/icon-glp1.webp',
    alt: 'healthy food and weightlifting icon',
    iconWidth: 80,
    tint: 'var(--tint-teal)',
  },
  {
    label: 'Improve your energy, overall health, and how you feel day to day',
    icon: '/images/icon-energy.webp',
    alt: 'lightning bolt icon',
    iconWidth: 75,
    tint: 'var(--tint-mint)',
  },
];

export const STEPS: readonly { title: string; body: string; pill: string }[] = [
  {
    title: 'Check your coverage',
    body: 'Answer a few quick questions to see if your insurance covers your visits—and what your cost may be, if anything.',
    pill: 'var(--chip-orange)',
  },
  {
    title: 'Book your first visit',
    body: 'We’ll help connect you to the right dietitian for you.',
    pill: 'var(--chip-yellow)',
  },
  {
    title: 'Meet virtually',
    body: 'Meet from home, on your schedule — join in whatever way feels comfortable for you.',
    pill: 'var(--chip-green)',
  },
  {
    title: 'Get ongoing support',
    body: 'You and your dietitian will work together to schedule follow-up visits based on your individual needs, health goals, and progress.',
    pill: 'var(--chip-teal)',
  },
];

export const MORE_THAN_VISITS = {
  heading: 'More than just nutrition visits',
  paragraphs: [
    'NourishedRx is built on the belief that food is essential to better health. We work at the intersection of nutrition, healthcare, and access—supporting individuals with personalized guidance and practical solutions that fit their everyday lives.',
    'Whether you are managing a chronic condition, looking for guidance on healthy eating, or trying to make lasting changes, our team of experts is here to help.',
  ],
} as const;

/** Outcome figures as published on the marketing site. `value` is the emphasised figure. */
export const STATS: readonly { value: string; label: string }[] = [
  { value: '1.5+', label: 'point decrease in A1C' },
  { value: '12+', label: 'pound sustained weight loss' },
  { value: '10%+', label: 'decrease in blood pressure' },
  { value: '96%', label: 'improve knowledge & confidence' },
];

export const CLOSING_CTA = {
  heading: 'See if you can get support from a dietitian for as little as $0',
  body: 'We’ll always let you know about any coverage or costs ahead of time—no surprises.',
  bodyStrong:
    'Take the first step toward personalized nutrition care with NourishedRx by clicking the button below.',
  contact: 'Call us at 833-349-0399 between 8 a.m. and 8 p.m. ET.',
  cta: 'Check your eligibility',
} as const;

export const TESTIMONIALS = {
  headingLead: 'Thousands of members trust NourishedRx to ',
  headingAccent: 'support their health.',
  quotes: [
    'I have to tell you how much I appreciate you and how much you’ve helped me. I really learned lot. I really really appreciate it. It’s nice to have someone who is a dietitian.',
    'To have a very knowledgeable dietitian giving advice and helping me set goals to work toward a healthier me was great!',
    'Talking to the dietitian was one Aha! moment in that I thought I was eating good but I was missing additional protein in my diet.',
    'I’m feeling physically better from eating the kinds of foods I want to eat that you’re getting me. I was a healthy eater before, but I’m feeling even stronger. It works — so many people could use you!',
    'After years of struggling, I realized the reason why I would wake up with high morning glucose numbers, my dietitian guided me!',
    'I appreciate you guys going above and beyond for your members. I really appreciate your services',
  ],
} as const;

export const FAQ: readonly FaqItem[] = [
  {
    question: 'Who is this for?',
    answer:
      'This is for anyone looking to improve their health with personalized nutrition support—whether that’s managing a condition, losing weight, or building healthier habits. This service is covered by certain health plans, and most people pay $0 (some plans may have a copay).',
  },
  {
    question: 'How much does it cost?',
    answer:
      'Many members pay $0, but it depends on your health plan. We’ll check your coverage and let you know before your visit.',
  },
  {
    // ADAPTED. The marketing site says: "After you submit your information, we'll verify your
    // benefits and follow up within about 2 days. We'll let you know your coverage details,
    // including how many sessions are included and if you have any copay."
    // That describes the legacy submit-and-follow-up flow. This app runs the payer eligibility
    // check in real time and shows the answer on the next screen, so the original text would
    // understate the product and set the wrong expectation.
    question: 'When will I know what my plan covers?',
    answer:
      'Right away. We check your benefits the moment you submit the form and show your coverage — including any copay — before you book your first visit.',
  },
  {
    // ADAPTED. The marketing site says: "No — we will not ask for any payment or credit card
    // information unless your plan includes a copay, and we'll always let you know ahead of time."
    // This app also asks for a card when a plan does not cover nutrition visits and the member
    // continues as self-pay, which is broader than "unless your plan includes a copay". Understating
    // when we ask for payment details is the one claim here worth getting exactly right.
    question: 'Do I need to enter a credit card?',
    answer:
      'Only if there is a cost to you. If your plan covers your visits in full, we never ask for a card. If you have a copay, or your plan does not cover nutrition visits and you choose to continue, we’ll ask you to save a card before you book — and we’ll always show you the amount first.',
  },
  {
    question: 'How long does it take to get scheduled?',
    answer:
      'Once your eligibility is confirmed, you can usually schedule right away based on availability.',
  },
  {
    question: 'What is the visit like?',
    answer:
      'You’ll meet with a dietitian over Zoom at a scheduled time that works for you. You’ll get personalized guidance and support.',
  },
  {
    question: 'How long are visits?',
    answer: '',
    bullets: ['First visit: 60 minutes', 'Follow-ups: 45 minutes'],
  },
  {
    question: 'Can I see the same dietitian each time?',
    answer:
      'Yes, we will do our best to keep your care consistent with the same dietitian. Please note scheduling availability may have an impact.',
  },
  {
    question: 'Do I have to commit to a program?',
    answer: 'No—there’s no long-term commitment. You can book visits as needed.',
  },
  {
    question: 'What if I need to cancel or miss an appointment?',
    answer:
      'Please let us know as soon as possible if you are unable to attend your consultation. Appointments that are missed or canceled the same day may be subject to a fee. Our consultations are in high demand, and we appreciate your consideration in allowing us to serve other members. You can cancel or reschedule at least 24 hours in advance. Missed appointments or same-day cancellations will result in a $75 fee.',
  },
];

export const FOOTER_COLUMNS: readonly {
  heading: string;
  links: readonly { label: string; href: string }[];
}[] = [
  {
    heading: 'Who We Support',
    links: [
      { label: 'Payers', href: 'https://www.nourishedrx.com/plans' },
      { label: 'Food Partners', href: 'https://www.nourishedrx.com/food-partners' },
      { label: 'Individuals', href: 'https://www.nourishedrx.com/individuals' },
    ],
  },
  {
    heading: 'Our Solution',
    links: [
      { label: 'Our Platform', href: 'https://www.nourishedrx.com/our-platform' },
      { label: 'Our Food Network', href: 'https://www.nourishedrx.com/food-network' },
      { label: 'Our Care Model', href: 'https://www.nourishedrx.com/our-care-model' },
    ],
  },
];

export const FOOTER_LEGAL: readonly { label: string; href: string }[] = [
  { label: 'Accessibility Statement', href: 'https://www.nourishedrx.com/accessibility-statement' },
  { label: 'Terms & Conditions', href: 'https://www.nourishedrx.com/terms-and-conditions' },
  { label: 'Privacy Policy', href: 'https://www.nourishedrx.com/privacy-policy' },
];

export const TRUST_BADGES: readonly { src: string; alt: string }[] = [
  { src: '/images/badge-soc.webp', alt: 'AICPA SOC for Service Organizations logo' },
  {
    src: '/images/badge-wosb.webp',
    alt: 'US small business administration woman-owned small business certified logo',
  },
  { src: '/images/badge-everify.webp', alt: 'E-Verify logo' },
];
